import { AnimatePresence } from 'motion/react';
import { useState } from 'react';

import { getDisplayName } from '@infinex/chain-config';
import { PasskeyIcon } from '@infinex/ui-icons';
import {
  Button,
  CalloutV2,
  ChainEmblem,
  Content,
  Footer,
  Text,
  useDialogRouteState,
} from '@infinex/ui/components';

import { requireSessionKey } from '../../../api/auth';
import { useGasDepositComplete } from '../../../api/gas/useGasDepositComplete';
import {
  calculateAssetDecimalPrecision,
  formatTokenAmount,
} from '../../../utils/amount';
import { AssetIcon } from '../../crates/AssetIcon';
import { CustodyEmblemFromWalletId } from '../../custody';
import { FormattedNumber, FormattedPrice } from '../../formatted-number';
import {
  FeeAccountRow,
  TransactionDetailRow,
  TransactionDetailRowsSection,
} from '../../transaction-details/TransactionDetailRow';
import { useCustodySelection } from '../../wallet-selector/WalletSelectedProvider';
import { useGasDeposit } from '../GasDepositQuoteProvider';
import { useGasDialogConfig } from '../GasDialogDependenciesContext';

export function GasTopUpConfirmation() {
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string>();

  const dialogRouteState = useDialogRouteState();
  const { selected } = useCustodySelection();
  const { gasQuote: quote, assetSelection, refetchGasQuote } = useGasDeposit();
  const { mutateAsync: depositGas } = useGasDepositComplete(refetchGasQuote);
  const { requiresSessionKey, onGasTopUpSubmit } = useGasDialogConfig();

  // Handler when user confirms in confirmation dialog
  const handleConfirmTopUp = async () => {
    if (quote?.status !== 'succeeded') return;
    if (!selected) return;
    setPending(true);

    onGasTopUpSubmit?.({
      topup_amount_usd: quote.gasAmount,
      asset_chain: quote.assetChain,
      asset_symbol: assetSelection.selectedAssetDetails?.symbol ?? '',
      asset_amount: String(
        formatTokenAmount(quote.assetAmount, quote.assetDecimals)
      ),
      custody_type: selected.custody,
      is_imported_wallet: selected.custody === 'wallet' && selected.isImported,
      quote_id: quote.quoteId,
    });

    try {
      await requireSessionKey();
      await depositGas({ quoteId: quote.quoteId });
      dialogRouteState?.push('topping-up');
    } catch (err) {
      if (err instanceof Error) {
        setErr(err.message);
      } else {
        setErr(
          'An unknown error occurred. Please contact support if it persists'
        );
      }
    } finally {
      setPending(false);
    }
  };

  if (quote?.status !== 'succeeded') {
    return null;
  }

  if (!selected) {
    return null;
  }

  const assetAmountValue = formatTokenAmount(
    quote.assetAmount,
    quote.assetDecimals
  );
  const assetDecimalPrecision = calculateAssetDecimalPrecision({
    tokenAmount: quote.assetAmount,
    tokenDecimals: quote.assetDecimals,
    rateUsd: quote.usdRate,
  });

  return (
    <>
      <Content className="flex-col">
        <div className="space-y-4">
          {/* Heading */}
          <div>
            <Text className="text-emphasis text-2xl font-semibold">
              Top up <FormattedPrice value={quote.gasAmount} prefixDollarSign />{' '}
              to Gas Account
            </Text>
          </div>

          {err && (
            <AnimatePresence>
              <CalloutV2 tone="critical">
                <Content>{err}</Content>
              </CalloutV2>
            </AnimatePresence>
          )}

          {/* Row-based details */}
          <TransactionDetailRowsSection>
            {/* Amount Row */}
            <TransactionDetailRow
              label="Amount"
              value={
                <div className="flex items-center gap-x-2">
                  <AssetIcon
                    chain={quote.assetChain}
                    address={quote.assetAddress}
                    size="xsmall"
                  />
                  <span className="text-sm font-semibold">
                    <FormattedNumber
                      value={assetAmountValue}
                      decimals={assetDecimalPrecision}
                    />{' '}
                    {assetSelection.selectedAssetDetails?.symbol ?? ''}
                  </span>
                </div>
              }
            />

            {/* Value Row */}
            <TransactionDetailRow
              label="Value"
              value={
                <span className="text-sm font-semibold">
                  <FormattedPrice value={quote.gasAmount} prefixDollarSign />
                </span>
              }
            />

            {/* Network Row */}
            <TransactionDetailRow
              label="Network"
              value={
                <div className="flex items-center gap-x-2">
                  <ChainEmblem of={quote.assetChain} size="xsmall" />
                  <span className="text-sm font-semibold">
                    {getDisplayName(quote.assetChain)}
                  </span>
                </div>
              }
            />

            {/* From Row */}
            <TransactionDetailRow
              label="From"
              value={
                <div className="flex items-center gap-x-1">
                  <CustodyEmblemFromWalletId
                    walletId={selected.id}
                    size="small"
                  />
                  <div className="text-emphasis text-sm font-semibold">
                    {selected.label}
                  </div>
                </div>
              }
            />

            {/* To Row */}
            <TransactionDetailRow
              label="To"
              value={
                <span className="text-sm font-semibold">
                  <FeeAccountRow />
                </span>
              }
            />

            {/* Network cost Row */}
            <TransactionDetailRow
              label="Network cost"
              value={<span className="text-sm font-semibold">Free</span>}
            />
          </TransactionDetailRowsSection>
        </div>
      </Content>

      <Footer className="flex flex-col gap-y-6">
        <span className="text-secondary text-center text-sm font-semibold">
          Please note that Gas Account top ups
          <br />
          cannot be refunded.
        </span>
        <Button
          size="large"
          prominence="high"
          onPress={handleConfirmTopUp}
          className="w-full"
          isPending={pending}
        >
          {requiresSessionKey && <PasskeyIcon />}
          <Text>Top up</Text>
        </Button>
      </Footer>
    </>
  );
}
